import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import {Inject, Injectable, OnDestroy, PLATFORM_ID} from '@angular/core';
import {BehaviorSubject, combineLatest, Subscription} from 'rxjs';
import {distinctUntilChanged, map} from 'rxjs/operators';
import {MobileExperienceConfigService} from './mobile-experience-config.service';
import {MobilePlatformPolicyService} from './mobile-platform-policy.service';

/**
 * The one application-level boundary for the opt-in mobile experience.
 *
 * It keeps platform and remote-flag checks in one place without changing the
 * lifecycle or containing block of SSR and PrimeNG layouts.
 */
@Injectable({providedIn: 'root'})
export class MobileExperienceShellService implements OnDestroy {
  private readonly activeSubject = new BehaviorSubject(false);
  private readonly subscriptions = new Subscription();
  private readonly isBrowser: boolean;
  private initialized = false;

  public readonly active$ = this.activeSubject.asObservable().pipe(distinctUntilChanged());

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) platformId: object,
    private readonly mobileConfig: MobileExperienceConfigService,
    private readonly mobilePlatform: MobilePlatformPolicyService,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  public initialize(): void {
    if (this.initialized || !this.isBrowser) {
      return;
    }
    this.initialized = true;
    this.subscriptions.add(combineLatest([
      this.mobileConfig.config$,
      this.mobilePlatform.isMobileExperience$,
    ]).pipe(
      map(([config, isMobilePlatform]) => config.mobileShellEnabled && isMobilePlatform),
      distinctUntilChanged(),
    ).subscribe((active) => this.setActive(active)));
  }

  public get isActive(): boolean {
    return this.activeSubject.value;
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.isBrowser) {
      this.document.documentElement.classList.remove('mobile-experience-shell');
    }
  }

  private setActive(active: boolean): void {
    this.activeSubject.next(active);
    this.document.documentElement.classList.toggle('mobile-experience-shell', active);
  }
}
